import { Wine, Sparkles, ShieldCheck, HeartHandshake, Wind, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const services = [
  {
    name: "Suchtprävention",
    icon: Wine,
    description:
      "Wenn Alkohol oder andere Gewohnheiten zu viel Raum einnehmen, schauen wir gemeinsam hin. Wir verstehen die Auslöser, bauen Stabilität auf und stärken deine Motivation zur Veränderung.",
    extra:
      "Viele merken erst spät, wenn aus einem „Glas zur Entspannung“ ein Muster wird. In der Suchtprävention geht es nicht um Druck oder Vorwürfe – sondern darum, früh genug gegenzusteuern. Wir klären, welche Rolle der Konsum in deinem Alltag spielt, was ihn auslöst und welche gesunden Alternativen dir wirklich helfen. Du entscheidest das Ziel: weniger trinken, pausieren oder ganz aufhören. Ich begleite dich ehrlich, diskret und ohne Stigma – in deinem Tempo.",
  },
  {
    name: "Persönliche Entwicklung",
    icon: Sparkles,
    description:
      "Du spürst, dass mehr in dir steckt? Gemeinsam lösen wir Blockaden, schärfen deine Ziele und stärken dein Selbstvertrauen.",
    extra: "Für mehr Klarheit und Selbstvertrauen im Alltag und im Beruf.",
  },
  {
    name: "Ängste und Unsicherheiten",
    icon: ShieldCheck,
    description:
      "Wenn Sorgen dich lähmen, schauen wir gemeinsam hin. Schritt für Schritt findest du zu mehr innerer Sicherheit zurück.",
    extra: "Wir gehen in deinem Tempo – ohne Überforderung, aber mit Fortschritt.",
  },
  {
    name: "Partnerschaft und Beziehungen",
    icon: HeartHandshake,
    description:
      "Ob Konflikte, Funkstille oder Neuanfang: Wir verbessern deine Kommunikation und finden Wege zu echter Nähe.",
    extra: "Für Paare und Einzelne, die ihre Beziehungen wieder spürbar machen wollen.",
  },
  {
    name: "Achtsamkeit und innere Balance",
    icon: Wind,
    description:
      "Raus aus dem Hamsterrad: Mit achtsamkeitsbasierten Übungen lernst du, Stress zu regulieren und wieder präsent zu leben.",
    extra: "Für mehr Gelassenheit und verlässliche Anker in deinem Alltag.",
  },
];

export default function Services() {
  return (
    <section
      id="schwerpunkte"
      data-testid="services-section"
      className="relative py-28 sm:py-36 bg-[#EAD0AE]/60"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <Reveal className="max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#93401D] mb-5">
              Beratungsschwerpunkte
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.02] text-[#29170C]">
              Fünf Felder.
              <br />
              <span className="italic text-[#93401D]">Ein gemeinsamer Weg.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#63432B] text-base max-w-md leading-relaxed">
              Dein Anliegen steht im Mittelpunkt. Jede Begleitung wird individuell
              auf dich zugeschnitten – auf deine Lebenssituation, dein Tempo und
              deine Ziele.
            </p>
          </Reveal>
        </div>

        {/* Featured: Suchtprävention (full width) */}
        <Reveal delay={0}>
          <article
            data-testid="service-card-suchtpraevention"
            className="group relative overflow-hidden rounded-[1.75rem] border bg-[#FCF4E8] border-[#EAD0AE] hover:border-[#E0BD93] p-8 sm:p-10 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(60,34,19,0.3)] mb-5"
          >
            <div className="flex flex-col sm:flex-row gap-8">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#F2DFC6] text-[#93401D] shrink-0">
                <Wine strokeWidth={1.5} size={26} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="font-serif leading-[1.1] tracking-tight text-3xl text-[#29170C]">
                    {services[0].name}
                  </h2>
                  <span className="text-[10px] tracking-[0.25em] uppercase bg-[#93401D]/10 text-[#93401D] px-3 py-1 rounded-full">
                    Schwerpunkt
                  </span>
                </div>
                <p className="mt-4 leading-relaxed text-[#63432B] text-[16px] max-w-3xl">
                  {services[0].description}
                </p>
                <p className="mt-4 leading-relaxed text-[#63432B] text-[15px] max-w-3xl">
                  {services[0].extra}
                </p>
                <p className="mt-5 rounded-xl bg-[#EAD0AE]/50 border border-[#E0BD93] px-5 py-4 text-[14px] leading-relaxed text-[#63432B] max-w-3xl">
                  <span className="font-medium text-[#29170C]">Wichtig:</span>{" "}
                  Meine Beratung versteht sich als psychologische Unterstützung
                  und Begleitung zur persönlichen Veränderung. Sie ersetzt keine
                  ärztliche, psychotherapeutische oder suchtmedizinische Behandlung.
                </p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-[#93401D]">
                  Mehr im Gespräch
                  <ArrowUpRight
                    size={14}
                    strokeWidth={1.5}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </div>
              </div>
            </div>
          </article>
        </Reveal>

        {/* Remaining 4 services in grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {services.slice(1).map((s, i) => (
            <ServiceCard key={s.name} service={s} delay={i * 0.08} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, delay = 0 }) {
  const Icon = service.icon;
  return (
    <Reveal delay={delay}>
      <article
        data-testid={`service-card-${service.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className="group relative h-full overflow-hidden rounded-[1.75rem] border bg-[#FCF4E8] border-[#EAD0AE] hover:border-[#E0BD93] p-7 sm:p-9 transition-all duration-500 flex flex-col hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(60,34,19,0.3)]"
      >
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-[#F2DFC6] text-[#93401D]">
          <Icon strokeWidth={1.5} size={22} />
        </div>

        <h2 className="font-serif mt-7 leading-[1.1] tracking-tight text-2xl text-[#29170C]">
          {service.name}
        </h2>

        <p className="mt-4 leading-relaxed text-[#63432B] text-[15px]">
          {service.description}
        </p>
        <p className="mt-3 leading-relaxed text-[#937353] text-[14px] italic">
          {service.extra}
        </p>

        <div className="mt-auto pt-8 inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-[#93401D]">
          Mehr im Gespräch
          <ArrowUpRight
            size={14}
            strokeWidth={1.5}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </div>
      </article>
    </Reveal>
  );
}
